import {
  StatusPill,
  StatusPillSelect,
  type StatusPillOption,
  type StatusPillSize,
} from '@/components/ui/status-pill-select';
import { useUpdateCandidateStatus } from '@/hooks/use-candidates';
import { usePermission } from '@/lib/permissions';
import type { CandidateStatus } from '@/types/candidates';
import { CANDIDATE_STATUS_TONE, formatStatusLabel } from '../lib/candidate-styles';
import { CONSENT_LOCKED_HINT } from '../lib/consent-actions';

const CANDIDATE_STATUS_OPTIONS: ReadonlyArray<CandidateStatus> = [
  'ACTIVE',
  'HIRING',
  'HIRED',
  'ARCHIVED',
];

interface CandidateStatusPillProps {
  candidateId: string;
  status: CandidateStatus;
  size?: StatusPillSize;
  /** When provided, the HIRED option is enabled and selecting it calls this
   *  instead of flipping status directly (HW-646 — opens the Hire dialog). */
  onHire?: () => void;
  /** Custom tooltip when HIRED is disabled (e.g. no open application). */
  hiredDisabledReason?: string;
  /** HW-1055: consent revoked — only archiving is left. */
  consentRevoked?: boolean;
}

export function CandidateStatusPill({
  candidateId,
  status,
  size = 'md',
  onHire,
  hiredDisabledReason,
  consentRevoked = false,
}: CandidateStatusPillProps) {
  const canEdit = usePermission('recruitment.candidates.edit');
  const mutation = useUpdateCandidateStatus(candidateId);

  if (!canEdit) {
    return (
      <StatusPill
        tone={CANDIDATE_STATUS_TONE[status]}
        label={formatStatusLabel(status)}
        size={size}
      />
    );
  }

  // HW-480/HW-646: a status-only flip to HIRED is never allowed — hiring must
  // create a linked Employee. On the vacancy board that happens via the Hired
  // stage; from the candidate profile the parent passes `onHire`, which opens
  // the Hire dialog. Without `onHire`, HIRED stays disabled.
  const hiredEnabled = !!onHire;
  const options: Array<StatusPillOption<CandidateStatus>> = CANDIDATE_STATUS_OPTIONS.map((s) => {
    // HW-1055: a candidate who revoked consent can still be archived, nothing else.
    if (consentRevoked && s !== 'ARCHIVED' && s !== status) {
      return { value: s, label: formatStatusLabel(s), disabled: true, disabledReason: CONSENT_LOCKED_HINT };
    }
    return {
      value: s,
      label: formatStatusLabel(s),
      disabled: s === 'HIRED' && status !== 'HIRED' && !hiredEnabled,
      disabledReason:
        s === 'HIRED' && status !== 'HIRED' && !hiredEnabled
          ? (hiredDisabledReason ?? 'Hire via the Hired stage on the vacancy board')
          : undefined,
    };
  });

  return (
    <StatusPillSelect
      value={status}
      label={formatStatusLabel(status)}
      tone={CANDIDATE_STATUS_TONE[status]}
      options={options}
      onChange={(next) => {
        if (next === 'HIRED' && onHire) {
          onHire();
          return;
        }
        mutation.mutate(next);
      }}
      size={size}
    />
  );
}
